import React from 'react';
import { 
  Download, 
  Smartphone, 
  Share2, 
  PlusSquare, 
  CheckCircle2, 
  X, 
  Zap, 
  ShieldCheck, 
  WifiOff 
} from 'lucide-react';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { Language } from '../i18n/translations';

interface PWAInstallModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang?: Language;
}

export const PWAInstallModal: React.FC<PWAInstallModalProps> = ({
  isOpen,
  onClose,
  lang = 'ja'
}) => {
  const { isInstallable, isInstalled, isIOS, install } = usePWAInstall();

  if (!isOpen) return null;

  const handleInstallClick = async () => {
    if (isInstallable) {
      const success = await install();
      if (success) {
        onClose();
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
      <div className="relative w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl overflow-hidden">
        {/* Ambient background glow */}
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-full bg-slate-800/60 hover:bg-slate-800 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header with App Icon */}
        <div className="flex items-center gap-3.5 mb-5">
          <img
            src="/pwa-192x192.png"
            alt="SymEnergy Logo"
            className="w-14 h-14 rounded-2xl shadow-lg border border-teal-500/30 object-cover shrink-0"
          />
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className="text-base font-bold text-white">
                {lang === 'ja' ? 'ホーム画面にインストール' : 'Install SymEnergy Native'}
              </h3>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              {lang === 'ja'
                ? '全画面ネイティブアプリとして端末に追加'
                : 'Add to home screen as standalone native app'}
            </p>
          </div>
        </div>

        {/* Key PWA Benefits */}
        <div className="grid grid-cols-3 gap-2 mb-5">
          <div className="p-2.5 rounded-xl bg-slate-950/60 border border-slate-800/80 text-center">
            <Zap className="w-4 h-4 text-teal-400 mx-auto mb-1" />
            <span className="text-[10px] font-medium text-slate-300 block">
              {lang === 'ja' ? '全画面表示' : 'Standalone'}
            </span>
            <span className="text-[9px] text-slate-500 block">
              {lang === 'ja' ? 'アドレスバーなし' : 'No browser bar'}
            </span>
          </div>

          <div className="p-2.5 rounded-xl bg-slate-950/60 border border-slate-800/80 text-center">
            <WifiOff className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
            <span className="text-[10px] font-medium text-slate-300 block">
              {lang === 'ja' ? '完全オフライン' : 'Offline Ready'}
            </span>
            <span className="text-[9px] text-slate-500 block">
              {lang === 'ja' ? '電波なしでも閲覧' : 'Cached SQLite'}
            </span>
          </div>

          <div className="p-2.5 rounded-xl bg-slate-950/60 border border-slate-800/80 text-center">
            <ShieldCheck className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
            <span className="text-[10px] font-medium text-slate-300 block">
              {lang === 'ja' ? '生体認証保護' : 'Biometrics'}
            </span>
            <span className="text-[9px] text-slate-500 block">
              {lang === 'ja' ? 'Face/Touch ID' : 'Hardware safe'}
            </span>
          </div>
        </div>

        {/* State 1: Already Installed */}
        {isInstalled ? (
          <div className="p-4 rounded-2xl bg-emerald-950/40 border border-emerald-800/40 text-center space-y-2">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto" />
            <h4 className="text-sm font-bold text-white">
              {lang === 'ja' ? 'インストール済みです' : 'Already Installed'}
            </h4>
            <p className="text-xs text-slate-300">
              {lang === 'ja'
                ? 'このアプリはすでにホーム画面またはスタンドアローン環境で実行されています。'
                : 'This app is already running in standalone mode from your home screen or desktop.'}
            </p>
            <button
              onClick={onClose}
              className="mt-2 w-full py-2.5 px-4 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-semibold transition"
            >
              {lang === 'ja' ? '閉じる' : 'Close'}
            </button>
          </div>
        ) : isInstallable ? (
          /* State 2: Chromium / Android direct install prompt available */
          <div className="space-y-3">
            <button
              onClick={handleInstallClick}
              className="w-full py-3 px-4 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-400 hover:to-emerald-500 text-slate-950 font-bold text-sm rounded-xl shadow-lg shadow-teal-950/50 flex items-center justify-center gap-2 transition active:scale-98"
            >
              <Download className="w-4 h-4" />
              <span>{lang === 'ja' ? '今すぐインストール' : 'Install Now'}</span>
            </button>
            <p className="text-[11px] text-center text-slate-400">
              {lang === 'ja'
                ? 'Chrome / Edge / Android の確認ダイアログが表示されます'
                : 'A prompt will ask for confirmation from your browser'}
            </p>
          </div>
        ) : isIOS ? (
          /* State 3: iOS Safari (WebKit does not support beforeinstallprompt) */
          <div className="space-y-3 p-4 rounded-2xl bg-slate-950/80 border border-slate-800">
            <div className="flex items-center gap-2 text-teal-400 text-xs font-bold">
              <Smartphone className="w-4 h-4" />
              <span>{lang === 'ja' ? 'iPhone / iPad (Safari) での手順' : 'iOS Safari Installation'}</span>
            </div>

            <ol className="space-y-2.5 text-xs text-slate-300">
              <li className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-slate-800 text-teal-400 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                  1
                </span>
                <div>
                  <span className="font-semibold text-white">
                    {lang === 'ja' ? '共有ボタン' : 'Share Button'}:
                  </span>{' '}
                  <span className="text-slate-300">
                    {lang === 'ja'
                      ? 'Safari画面下部の「共有」アイコン'
                      : 'Tap the Share icon'}
                  </span>{' '}
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-800 text-teal-400 text-[11px] align-middle">
                    <Share2 className="w-3 h-3 inline mr-1" />
                    {lang === 'ja' ? '共有' : 'Share'}
                  </span>{' '}
                  {lang === 'ja' ? 'をタップします。' : 'in the toolbar.'}
                </div>
              </li>

              <li className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-slate-800 text-teal-400 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                  2
                </span>
                <div>
                  <span className="font-semibold text-white">
                    {lang === 'ja' ? 'ホーム画面に追加' : 'Add to Home Screen'}:
                  </span>{' '}
                  <span className="text-slate-300">
                    {lang === 'ja'
                      ? 'メニューをスクロールして'
                      : 'Scroll down and tap'}
                  </span>{' '}
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-800 text-teal-400 text-[11px] align-middle">
                    <PlusSquare className="w-3 h-3 inline mr-1" />
                    {lang === 'ja' ? 'ホーム画面に追加' : 'Add to Home Screen'}
                  </span>{' '}
                  {lang === 'ja' ? 'を選択します。' : '.'}
                </div>
              </li>

              <li className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-slate-800 text-teal-400 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                  3
                </span>
                <div>
                  <span className="font-semibold text-white">
                    {lang === 'ja' ? '完了' : 'Launch'}:
                  </span>{' '}
                  {lang === 'ja'
                    ? '右上の「追加」をタップすると、ホーム画面にネイティブアプリアイコンが作成されます。'
                    : 'Tap "Add" in top-right. The app icon will appear on your home screen.'}
                </div>
              </li>
            </ol>

            <button
              onClick={onClose}
              className="mt-3 w-full py-2.5 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded-xl text-xs transition"
            >
              {lang === 'ja' ? '了解しました' : 'Got it'}
            </button>
          </div>
        ) : (
          /* State 4: Desktop Chrome / Firefox / Safari fallback guide */
          <div className="space-y-3 p-4 rounded-2xl bg-slate-950/80 border border-slate-800 text-center">
            <p className="text-xs text-slate-300">
              {lang === 'ja'
                ? 'ブラウザのアドレスバー右側にある「インストール」アイコン（またはメニューの「ホーム画面に追加 / アプリをインストール」）をクリックしてください。'
                : 'Click the "Install" icon in your browser URL bar, or choose "Add to Home Screen / Install App" from the browser menu.'}
            </p>
            <button
              onClick={onClose}
              className="w-full py-2.5 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded-xl text-xs transition"
            >
              {lang === 'ja' ? '了解しました' : 'Got it'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
